import React from 'react';
import styled from 'styled-components';

const ErrorContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.error}15;
  border: 1px solid ${({ theme }) => theme.colors.error}40;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  transition: all ${({ theme }) => theme.transitions.gallery.hover};
  
  /* Mobile-first responsive design */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${({ theme }) => theme.spacing.mobile.sm};
    padding: ${({ theme }) => theme.spacing.mobile.padding};
    background-color: ${({ theme }) => theme.colors.error}12;
    border: 2px solid ${({ theme }) => theme.colors.error}30;
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    margin-bottom: ${({ theme }) => theme.spacing.mobile.lg};
    box-shadow: ${({ theme }) => theme.shadows.gallery.subtle};
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.md};
    padding: ${({ theme }) => theme.spacing.lg};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
  }
  
  /* Enhanced desktop styling */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
    box-shadow: ${({ theme }) => theme.shadows.gallery.soft};
    
    &:hover {
      box-shadow: ${({ theme }) => theme.shadows.gallery.comfortable};
      transform: translateY(-1px);
    }
  }
`;

const ErrorIcon = styled.span`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  flex-shrink: 0;
  line-height: 1;
  
  /* Mobile-first responsive sizing */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    align-self: flex-start;
    margin-top: 2px; /* Align with text baseline */
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
  
  /* Enhanced desktop styling */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }
`;

const ErrorContent = styled.div`
  flex: 1;
  color: ${({ theme }) => theme.colors.error};
  min-width: 0; /* Allow text wrapping */
  
  /* Mobile-first spacing */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    order: 2; /* Content comes after icon on mobile */
  }
`;

const ErrorTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  line-height: ${({ theme }) => theme.typography.lineHeight.snug};
  
  /* Mobile-first typography */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.mobile.heading};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.semibold};
    margin-bottom: ${({ theme }) => theme.spacing.mobile.xs};
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
  
  /* Enhanced desktop typography */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.metadata};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.bold};
  }
`;

const ErrorMessage = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
  
  /* Mobile-first typography */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.mobile.body};
    line-height: ${({ theme }) => theme.typography.lineHeight.loose};
    word-break: break-word;
    hyphens: auto;
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
  
  /* Enhanced desktop readability */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.caption};
  }
`;

const ErrorList = styled.ul`
  margin: ${({ theme }) => theme.spacing.xs} 0 0 ${({ theme }) => theme.spacing.md};
  padding: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ErrorListItem = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.error};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 1;
  transition: all ${({ theme }) => theme.transitions.gallery.quick};
  flex-shrink: 0;
  min-width: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ theme }) => theme.colors.error}20;
    transform: scale(1.05);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.error};
    outline-offset: 2px;
  }
  
  /* Enhanced mobile touch target */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: ${({ theme }) => theme.breakpoints.touchTarget};
    min-height: ${({ theme }) => theme.breakpoints.touchTarget};
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    padding: ${({ theme }) => theme.spacing.mobile.sm};
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    order: 1; /* Dismiss button comes first on mobile */
    align-self: flex-end;
    
    &:active {
      transform: scale(0.95);
      background-color: ${({ theme }) => theme.colors.error}30;
    }
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 36px;
    min-height: 36px;
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
  
  /* Enhanced desktop interaction */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    border-radius: ${({ theme }) => theme.borderRadius.gallery.subtle};
    
    &:hover {
      box-shadow: ${({ theme }) => theme.shadows.gallery.subtle};
    }
  }
`;

export interface InlineErrorProps {
  error: string | Error | { message: string; errors?: Record<string, string[]> };
  title?: string;
  onDismiss?: () => void;
  className?: string;
}

export function InlineError({ error, title, onDismiss, className }: InlineErrorProps) {
  // Normalize error to a consistent format
  const normalizedError = React.useMemo(() => {
    if (typeof error === 'string') {
      return { message: error, errors: undefined };
    }
    
    if (error instanceof Error) {
      return { message: error.message, errors: undefined };
    }
    
    if (error && typeof error === 'object' && 'message' in error) {
      return {
        message: error.message,
        errors: 'errors' in error ? error.errors : undefined,
      };
    }
    
    return { message: 'An unknown error occurred', errors: undefined };
  }, [error]);

  if (!error) return null;

  return (
    <ErrorContainer className={className}>
      <ErrorIcon>⚠️</ErrorIcon>
      <ErrorContent>
        {title && <ErrorTitle>{title}</ErrorTitle>}
        <ErrorMessage>{normalizedError.message}</ErrorMessage>
        
        {normalizedError.errors && Object.keys(normalizedError.errors).length > 0 && (
          <ErrorList>
            {Object.entries(normalizedError.errors).map(([field, fieldErrors]) => 
              fieldErrors.map((fieldError, index) => (
                <ErrorListItem key={`${field}-${index}`}>
                  <strong>{field}:</strong> {fieldError}
                </ErrorListItem>
              ))
            )}
          </ErrorList>
        )}
      </ErrorContent>
      
      {onDismiss && (
        <DismissButton 
          onClick={onDismiss}
          aria-label="Dismiss error"
          title="Dismiss"
        >
          ×
        </DismissButton>
      )}
    </ErrorContainer>
  );
}

// Hook for managing inline errors with auto-dismiss
export function useInlineError(autoDismissMs: number = 0) {
  const [error, setError] = React.useState<InlineErrorProps['error'] | null>(null);

  const showError = React.useCallback((newError: InlineErrorProps['error']) => {
    setError(newError);
    
    if (autoDismissMs > 0) {
      const timeout = setTimeout(() => setError(null), autoDismissMs);
      return () => clearTimeout(timeout);
    }
  }, [autoDismissMs]);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    showError,
    clearError,
    ErrorComponent: error ? (
      <InlineError error={error} onDismiss={clearError} />
    ) : null,
  };
}

// Specialized error components
export function ApiError({ 
  error, 
  title = "API Error", 
  onDismiss 
}: Omit<InlineErrorProps, 'error'> & { 
  error: { message: string; status?: number; errors?: Record<string, string[]> }
}) {
  const message = error.status 
    ? `${error.message} (Status: ${error.status})`
    : error.message;

  return (
    <InlineError
      error={{ message, errors: error.errors }}
      title={title}
      onDismiss={onDismiss}
    />
  );
}

export function ValidationError({
  errors,
  title = "Please fix the following errors:",
  onDismiss
}: {
  errors: Record<string, string[]>;
  title?: string;
  onDismiss?: () => void;
}) {
  return (
    <InlineError
      error={{ message: '', errors }}
      title={title}
      onDismiss={onDismiss}
    />
  );
}